import { queryOptions } from '@tanstack/react-query'
import type {
  BroadcastListResponse,
  BroadcastDetail,
  BroadcastDeliveryResponse,
  CreateBroadcastRequest,
  BroadcastMessage,
} from '@/types/broadcastTypes'

// List all broadcasts for the rail. Polls so new sends / replies surface.
export const broadcastsQueryOptions = () =>
  queryOptions({
    queryKey: ['broadcasts'],
    queryFn: async (): Promise<BroadcastListResponse> => {
      const response = await fetch('/api/broadcasts')
      if (!response.ok) {
        throw new Error('Failed to fetch broadcasts')
      }
      return response.json()
    },
    refetchInterval: 15000,
  })

// A single broadcast thread (initial message + follow-ups + inbound replies).
export const broadcastQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['broadcast', id],
    queryFn: async (): Promise<BroadcastDetail> => {
      const response = await fetch(`/api/broadcasts/${id}`)
      if (!response.ok) {
        throw new Error('Failed to fetch broadcast')
      }
      return response.json()
    },
    enabled: !!id,
    refetchInterval: 5000,
  })

// Per-recipient delivery breakdown. Polls so status callbacks surface.
export const broadcastDeliveryQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['broadcastDelivery', id],
    queryFn: async (): Promise<BroadcastDeliveryResponse> => {
      const response = await fetch(`/api/broadcasts/${id}/delivery`)
      if (!response.ok) {
        throw new Error('Failed to fetch delivery status')
      }
      return response.json()
    },
    enabled: !!id,
    refetchInterval: 5000,
  })

export const createBroadcast = async (
  data: CreateBroadcastRequest,
): Promise<BroadcastDetail> => {
  const response = await fetch('/api/broadcasts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to create broadcast')
  }
  return response.json()
}

export const sendBroadcastFollowup = async (
  id: string,
  message: string,
): Promise<BroadcastMessage> => {
  const response = await fetch(`/api/broadcasts/${id}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to send message')
  }
  return response.json()
}

export const archiveBroadcast = async (id: string): Promise<void> => {
  const response = await fetch(`/api/broadcasts/${id}`, { method: 'DELETE' })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.error || 'Failed to archive broadcast')
  }
}
